import { useState } from 'react';
import { auth } from '../api/endpoints';
import { setToken } from '../api/client';
import type { Admin } from '../api/types';
import logoWhite from '../assets/Logo_White.svg';

interface LoginPageProps {
  onSuccess: (admin: Admin) => void;
}

type View = 'login' | 'forgot' | 'forgot-sent' | 'reset' | 'reset-done';

const input = { width: '100%', border: '1px solid #333', borderRadius: '8px', padding: '10px 14px', fontSize: '14px', background: '#0d0d0d', color: '#fff', outline: 'none', boxSizing: 'border-box' } as const;
const label = { display: 'block', color: '#999', fontSize: '12px', marginBottom: '6px', fontWeight: 500 } as const;
const linkButton = { background: 'none', border: 'none', color: '#999', fontSize: '13px', cursor: 'pointer', padding: 0, textDecoration: 'underline' } as const;

export default function LoginPage({ onSuccess }: LoginPageProps) {
  // The emailed link opens the panel at /?reset=<token>.
  const resetToken = new URLSearchParams(window.location.search).get('reset') || '';

  const [view, setView] = useState<View>(resetToken ? 'reset' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await auth.login(email, password);
      setToken(res.token);
      onSuccess(res.admin);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await auth.forgotPassword(email.trim());
      setView('forgot-sent');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not send the reset link');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 12) return setError('Use at least 12 characters.');
    if (password !== confirm) return setError('The two passwords do not match.');
    setLoading(true);
    setError('');
    try {
      await auth.resetPassword(resetToken, password);
      window.history.replaceState({}, '', window.location.pathname);
      setPassword('');
      setConfirm('');
      setView('reset-done');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not reset the password');
    } finally {
      setLoading(false);
    }
  };

  const submitStyle = (disabled: boolean) => ({ width: '100%', background: '#D1004B', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px', fontSize: '14px', fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }) as const;

  const heading: Record<View, [string, string]> = {
    login: ['Admin console', 'Sign in to your account'],
    forgot: ['Forgot your password?', 'Enter your email and we will send you a reset link'],
    'forgot-sent': ['Check your inbox', 'If that address belongs to an admin account, a reset link is on its way. It is valid for one hour.'],
    reset: ['Choose a new password', 'Use at least 12 characters'],
    'reset-done': ['Password updated', 'You can now sign in with your new password'],
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0d0d0d' }}>
      <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: '16px', padding: '48px', width: '400px', maxWidth: '90vw' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src={logoWhite} alt="Donatella" height={26} width={Math.round((840 / 117) * 26)} style={{ display: 'block', margin: '0 auto 20px' }} draggable={false} />
          <h1 style={{ color: '#fff', fontSize: '20px', fontWeight: 700, margin: '0 0 8px' }}>{heading[view][0]}</h1>
          <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>{heading[view][1]}</p>
        </div>

        {view === 'login' && (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="login-email" style={label}>Email</label>
              <input id="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@donatella.sa" required autoComplete="email" autoFocus style={input} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label htmlFor="login-password" style={label}>Password</label>
              <input id="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required autoComplete="current-password" style={input} />
            </div>
            <div style={{ marginBottom: '20px', textAlign: 'right' }}>
              <button type="button" onClick={() => { setError(''); setView('forgot'); }} style={linkButton}>Forgot password?</button>
            </div>
            {error && <p role="alert" style={{ color: '#ff6b6b', fontSize: '13px', marginBottom: '16px', padding: '10px 14px', background: 'rgba(255,107,107,0.1)', borderRadius: '8px' }}>{error}</p>}
            <button type="submit" disabled={loading} style={submitStyle(loading)}>{loading ? 'Signing in...' : 'Sign in'}</button>
          </form>
        )}

        {view === 'forgot' && (
          <form onSubmit={handleForgot}>
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="forgot-email" style={label}>Email</label>
              <input id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@donatella.sa" required autoComplete="email" autoFocus style={input} />
            </div>
            {error && <p role="alert" style={{ color: '#ff6b6b', fontSize: '13px', marginBottom: '16px', padding: '10px 14px', background: 'rgba(255,107,107,0.1)', borderRadius: '8px' }}>{error}</p>}
            <button type="submit" disabled={loading} style={submitStyle(loading)}>{loading ? 'Sending...' : 'Send reset link'}</button>
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <button type="button" onClick={() => { setError(''); setView('login'); }} style={linkButton}>Back to sign in</button>
            </div>
          </form>
        )}

        {(view === 'forgot-sent' || view === 'reset-done') && (
          <button type="button" onClick={() => setView('login')} style={submitStyle(false)}>Back to sign in</button>
        )}

        {view === 'reset' && (
          <form onSubmit={handleReset}>
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="reset-password" style={label}>New password</label>
              <input id="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" autoFocus style={input} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="reset-confirm" style={label}>Confirm new password</label>
              <input id="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" style={input} />
            </div>
            {error && <p role="alert" style={{ color: '#ff6b6b', fontSize: '13px', marginBottom: '16px', padding: '10px 14px', background: 'rgba(255,107,107,0.1)', borderRadius: '8px' }}>{error}</p>}
            <button type="submit" disabled={loading} style={submitStyle(loading)}>{loading ? 'Saving...' : 'Update password'}</button>
          </form>
        )}
      </div>
    </div>
  );
}
